import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, QuestionCircle, Sliders, SortDown, SortUp, X } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { MOBILE_UI } from '../../constants/mobileUi';
import useIsMobile from '../../hooks/useIsMobile';
import BottomSheet from '../shared/BottomSheet';
import FilterChip from '../shared/FilterChip';
import FilterChipBar from '../shared/FilterChipBar';
import SegmentedControl from '../shared/SegmentedControl';
import SheetOptionList from '../shared/SheetOptionList';
import SheetSection from '../shared/SheetSection';
import { useWatchlistSourceMenu } from '../shared/WatchlistSourceSelect';
import {
  getRsTimeframe,
  RS_MAX_MULTI_TIMEFRAMES,
  RS_MULTI_TIMEFRAME_OPTIONS,
} from '../../constants/relativeStrengthTimeframes';

const VIEW_OPTIONS = [
  { value: 'single', label: 'Simple', testId: 'relative-strength-view-single' },
  { value: 'multi', label: 'Detailed', testId: 'relative-strength-view-multi' },
];

function DesktopControlPanel({ show, title, onHide, testId, closeLabel, footer, children, colors }) {
  if (!show) return null;
  return (
    <div
      role="dialog"
      aria-label={title}
      data-testid={testId}
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        right: 0,
        zIndex: 45,
        width: 380,
        maxWidth: 'calc(100vw - 48px)',
        maxHeight: 'min(70vh, 620px)',
        overflowY: 'auto',
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        backgroundColor: colors.bgPrimary,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 44,
          padding: '6px 8px 6px 14px',
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.bgPrimary,
        }}
      >
        <strong style={{ color: colors.text, fontSize: 14 }}>{title}</strong>
        <button
          type="button"
          aria-label={closeLabel}
          onClick={onHide}
          style={{
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            borderRadius: 8,
            background: 'transparent',
            color: colors.textMuted,
            cursor: 'pointer',
          }}
        >
          <X size={18} />
        </button>
      </div>
      <div style={{ padding: '12px 14px' }}>{children}</div>
      {footer ? (
        <div style={{ padding: '8px 14px', borderTop: `1px solid ${colors.border}` }}>
          {footer}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Shared Relative Strength toolbar: four chips in one row. Mobile uses bottom sheets;
 * desktop keeps the same controls in an anchored panel.
 */
export default function RelativeStrengthMobileToolbar({
  sourceOptions,
  tickerSource,
  onTickerSourceChange,
  onAddTicker,
  relativeTo,
  relativeToSelectOptions,
  onRelativeToChange,
  sortDirection,
  onSortDirectionToggle,
  isMulti,
  onModeChange,
  showTimeframe = true,
  timeframeKey,
  timeframeOptions,
  onTimeframeChange,
  multiKeys,
  onMultiTimeframeToggle,
  sortColumn,
  sortColumnOptions,
  onSortColumnChange,
  onShowMethodology,
}) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [openSheet, setOpenSheet] = useState(null);
  const rootRef = useRef(null);
  const closeSheet = () => setOpenSheet(null);
  const listMenu = useWatchlistSourceMenu({
    value: tickerSource,
    onChange: (next) => {
      onTickerSourceChange?.(next);
    },
    options: sourceOptions,
    testId: 'relative-strength-source',
    sheetTestId: 'relative-strength-list-sheet',
    onBeforeOpen: closeSheet,
  });

  useEffect(() => {
    if (!openSheet || isMobile) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) closeSheet();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeSheet();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobile, openSheet]);

  const benchmarkOptions = useMemo(
    () => (relativeToSelectOptions || []).map((option) => ({
      value: option.value,
      label: option.label,
      hint: option.label === option.value ? null : option.value,
    })),
    [relativeToSelectOptions],
  );

  const displayValue = isMulti
    ? 'Detailed'
    : (showTimeframe ? getRsTimeframe(timeframeKey).label : 'Simple');

  const benchmarkContent = (
    <SheetOptionList
      options={benchmarkOptions}
      value={relativeTo}
      onChange={(next) => {
        onRelativeToChange?.(next);
        closeSheet();
      }}
      ariaLabel="vs"
    />
  );

  const methodologyButton = (
    <button
      type="button"
      data-testid="relative-strength-info"
      onClick={() => {
        closeSheet();
        onShowMethodology?.();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        minHeight: isMobile ? MOBILE_UI.touchMin : 32,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: colors.primary,
        fontSize: MOBILE_UI.font.body,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      <QuestionCircle size={15} />
      How relative strength works
    </button>
  );

  const displayContent = (
    <>
      <SheetSection title="View">
        <SegmentedControl
          options={VIEW_OPTIONS}
          value={isMulti ? 'multi' : 'single'}
          onChange={onModeChange}
          variant="accent"
          ariaLabel="Relative strength view"
          testId="relative-strength-view-toggle"
        />
      </SheetSection>

      {isMulti ? (
        <>
          <SheetSection
            title={`Columns · up to ${RS_MAX_MULTI_TIMEFRAMES}`}
            hint="Columns run shortest to longest. Use 15m / 1h for intraday RS."
          >
            <SheetOptionList
              options={RS_MULTI_TIMEFRAME_OPTIONS.map((tf) => ({ value: tf.key, label: tf.label }))}
              value={multiKeys}
              onChange={onMultiTimeframeToggle}
              multi
              max={RS_MAX_MULTI_TIMEFRAMES}
              ariaLabel="Timeframe columns"
              testId="relative-strength-multi-timeframes"
              getTestId={(option) => `relative-strength-multi-tf-${option.value}`}
            />
          </SheetSection>
          <SheetSection title="Sort by">
            <SheetOptionList
              options={sortColumnOptions}
              value={sortColumn}
              onChange={onSortColumnChange}
              ariaLabel="Sort by column"
              testId="relative-strength-sort-column"
            />
          </SheetSection>
        </>
      ) : showTimeframe ? (
        <SheetSection title="Timeframe">
          <SheetOptionList
            options={timeframeOptions}
            value={timeframeKey}
            onChange={onTimeframeChange}
            ariaLabel="Timeframe"
            testId="relative-strength-timeframe"
            getTestId={(option) => `relative-strength-timeframe-${option.value}`}
          />
        </SheetSection>
      ) : null}
    </>
  );

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <FilterChipBar
        sticky={isMobile}
        bleed={isMobile ? MOBILE_UI.pagePadding : 0}
        testId="relative-strength-toolbar"
        ariaLabel="Relative strength filters"
      >
        <button
          type="button"
          data-testid="relative-strength-add-ticker"
          aria-label="Add ticker to this list"
          title="Add ticker"
          onClick={onAddTicker}
          style={{
            flex: '0 0 auto',
            width: MOBILE_UI.chipHeight,
            height: MOBILE_UI.chipHeight,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            borderRadius: MOBILE_UI.radiusChip,
            backgroundColor: colors.primary,
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <Plus size={18} />
        </button>
        {listMenu.chip}
        <FilterChip
          testId="relative-to-select"
          ariaLabel="vs"
          ariaExpanded={openSheet === 'vs'}
          label="vs"
          value={relativeTo}
          active={openSheet === 'vs'}
          onClick={() => {
            listMenu.close();
            setOpenSheet((current) => (current === 'vs' ? null : 'vs'));
          }}
        />
        <FilterChip
          testId="relative-strength-sort"
          ariaLabel={sortDirection === 'strength' ? 'Sorted by strength' : 'Sorted by weakness'}
          title={sortDirection === 'strength' ? 'Strongest first' : 'Weakest first'}
          value={sortDirection === 'strength' ? 'Strong' : 'Weak'}
          leadingIcon={sortDirection === 'strength' ? <SortDown size={14} /> : <SortUp size={14} />}
          trailing="none"
          active={sortDirection === 'weakness'}
          onClick={onSortDirectionToggle}
        />
        <FilterChip
          testId="relative-strength-settings-button"
          ariaLabel="Relative strength display settings"
          ariaExpanded={openSheet === 'display'}
          value={displayValue}
          leadingIcon={<Sliders size={13} />}
          active={openSheet === 'display'}
          onClick={() => {
            listMenu.close();
            setOpenSheet((current) => (current === 'display' ? null : 'display'));
          }}
        />
      </FilterChipBar>
      {listMenu.panel}

      {isMobile ? (
        <>
          <BottomSheet
            show={openSheet === 'vs'}
            onHide={closeSheet}
            title="Compare vs"
            testId="relative-strength-benchmark-sheet"
            closeLabel="Close benchmark picker"
          >
            {benchmarkContent}
          </BottomSheet>
          <BottomSheet
            show={openSheet === 'display'}
            onHide={closeSheet}
            title="Display"
            testId="relative-strength-display-sheet"
            closeLabel="Close display settings"
            footer={methodologyButton}
          >
            {displayContent}
          </BottomSheet>
        </>
      ) : (
        <>
          <DesktopControlPanel
            show={openSheet === 'vs'}
            title="Compare vs"
            onHide={closeSheet}
            testId="relative-strength-benchmark-sheet"
            closeLabel="Close benchmark picker"
            colors={colors}
          >
            {benchmarkContent}
          </DesktopControlPanel>
          <DesktopControlPanel
            show={openSheet === 'display'}
            title="Display"
            onHide={closeSheet}
            testId="relative-strength-display-sheet"
            closeLabel="Close display settings"
            footer={methodologyButton}
            colors={colors}
          >
            {displayContent}
          </DesktopControlPanel>
        </>
      )}
    </div>
  );
}
